import Vue from 'vue';
import Vuex from 'vuex';
import {
  getTerminalAPI,
  getBarcodesAPI,
  getBarcodesLoginAPI,
  getComparesAPI,
  getConvertsAPI,
  getInputsAPI,
  getReadsAPI,
  getReadLoginAPI
} from '@/api';

Vue.use(Vuex);

export default new Vuex.Store({
  state: {
    loggedIn: false,      // ログイン状態
    historyCount: 0,      // ページ履歴件数
    term: null,           // 端末情報
    userName: null,       // 担当者名
    converts: [],         // 変換情報配列
    reads: [],            // 読取情報配列
    inputs: [],           // 取込情報配列
    barcodes: [],         // バーコード情報配列
    compares: [],         // 照合情報配列
    currentRead: null,    // 現在の読取情報
    currentCompare: null, // 現在の照合情報
    currentInput: null,   // 現在の取込情報
    message: null         // メッセージ表示
  },
  getters: {
    // ログイン状態取得
    loggedIn: function (state) {
      return state.loggedIn;
    },
    // ページ履歴件数取得
    historyCount: function (state) {
      return state.historyCount;
    },
    // 端末情報取得
    term: function (state) {
      return state.term;
    },
    // 担当者名取得
    userName: function (state) {
      return state.userName;
    },
    // 変換情報配列取得
    converts: function (state) {
      return state.converts;
    },
    // 読取情報配列取得
    reads: function (state) {
      return state.reads;
    },
    // 取込情報配列取得
    inputs: function (state) {
      return state.inputs;
    },
    // バーコード情報配列
    barcodes: function (state) {
      return state.barcodes;
    },
    // 照合情報配列
    compares: function (state) {
      return state.compares;
    },
    // 現在の読取情報
    currentRead: function (state) {
      return state.currentRead;
    },
    // 現在の照合情報
    currentCompare: function (state) {
      return state.currentCompare;
    },
    // 現在の取込情報
    currentInput: function (state) {
      return state.currentInput;
    },
    // メッセージ表示
    message: function (state) {
      return state.message;
    }
  },
  mutations: {
    // ログイン状態セット
    setLoggedIn(state, loggedIn) {
      state.loggedIn = loggedIn;
    },
    // ページ履歴件数加算
    incrementHistoryCount(state) {
      state.historyCount++;
    },
    // ページ履歴件数減算
    decrementHistoryCount(state) {
      state.historyCount--;
    },
    // ページ履歴件数クリア
    clearHistoryCount(state) {
      state.historyCount = 0;
    },
    // 端末情報セット
    setTerm(state, term) {
      state.term = term;
    },
    // 担当者名セット
    setUserName(state, userName) {
      state.userName = userName;
    },
    // 変換情報配列セット
    setConverts(state, converts) {
      state.converts = converts;
    },
    // 読取情報配列セット
    setReads(state, reads) {
      state.reads = reads;
      state.currentRead = null;
    },
    // 取込情報配列セット
    setInputs(state, inputs) {
      state.inputs = inputs;
      state.currentInput = null;
    },
    // バーコード情報配列
    setBarcodes(state, barcodes) {
      state.barcodes = barcodes;
    },
    // 照合情報配列
    setCompares(state, compares) {
      state.compares = compares;
      state.currentCompare = null;
    },
    // 読取IDセット
    setReadId(state, readId) {
      if (readId) {
        // 現在の読取情報をセット
        state.currentRead = state.reads.find(function (x) {
          return x.readId === readId;
        });
      } else {
        state.currentRead = null;
      }

      // 現在の照合情報をセット
      if (state.currentRead) {
        state.currentCompare = state.compares.find(function (x) {
          return x.compareId === state.currentRead.compareId;
        });
      } else {
        state.currentCompare = null;
      }
    },
    // 取込IDセット
    setInputId(state, inputId) {
      // 現在の取込情報をセット
      if (inputId) {
        state.currentInput = state.inputs.find(function (x) {
          return x.inputId === inputId;
        });
      } else {
        state.currentInput = null;
      }

      // 現在の照合情報をセット
      if (state.currentInput) {
        state.currentCompare = state.compares.find(function (x) {
          return x.compareId === state.currentInput.compareId;
        });
      } else {
        state.currentCompare = null;
      }

      // 現在の読取情報をセット
      if (state.currentCompare) {
        state.currentRead = state.reads.find(function (x) {
          return x.compareId === state.currentCompare.compareId;
        });
      } else {
        state.currentRead = null;
      }
    },
    // メッセージ設定
    setMessage(state, message) {
      state.message = message;
    }
  },
  actions: {
    // 端末情報セット
    async setTerminalAction({ commit }, termId) {
      const ret = await getTerminalAPI(termId);
      if (!ret) return;
      commit('setTerm', ret.data);
    },
    // ログインステートセット
    async setLoginStateAction({ commit }, user) {
      commit('setLoggedIn', true);
      commit('setUserName', user.userName);

      // 変換情報取得API
      let ret = await getConvertsAPI();
      if (!ret) return;
      commit('setConverts', ret.data);

      // 読取情報取得API
      ret = await getReadsAPI();
      if (!ret) return;
      commit('setReads', ret.data);

      // 取込情報取得API
      ret = await getInputsAPI();
      if (!ret) return;
      commit('setInputs', ret.data);

      // バーコード情報取得API
      ret = await getBarcodesAPI();
      if (!ret) return;
      commit('setBarcodes', ret.data);

      // 照合情報取得API
      ret = await getComparesAPI();
      if (!ret) return;
      commit('setCompares', ret.data);
    },
    // ログアウトステートセット
    async setLogoutStateAction({ commit }) {
      commit('clearHistoryCount');
      commit('setLoggedIn', false);
      commit('setUserName', null);

      // 変換情報はクリア
      commit('setConverts', []);

      // 照合情報はクリア
      commit('setCompares', []);

      // 取込情報はクリア
      commit('setInputs', []);

      // 読取情報取得API(ログイン前)
      let ret = await getReadLoginAPI();
      if (!ret) return;
      commit('setReads', ret.data);
      if (ret.data.length > 0) commit('setReadId', ret.data[0].readId);

      // バーコード情報取得API(ログイン前)
      ret = await getBarcodesLoginAPI();
      if (!ret) return;
      commit('setBarcodes', ret.data);
    }
  }
});
